import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { workflowStagesService, tasksService, communicationsService, clientsService } from '../../services';
import './WorkflowAnalytics.css';

const WorkflowAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days
  const [analytics, setAnalytics] = useState({
    workflow: {},
    tasks: {},
    communications: {},
    clients: {}
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const [
        workflowStages,
        allTasks,
        allCommunications,
        allClients,
        taskStats,
        commStats
      ] = await Promise.all([
        workflowStagesService.getStages(),
        tasksService.getTasks(),
        communicationsService.getCommunications({
          date_from: startDate.toISOString().split('T')[0],
          date_to: endDate.toISOString().split('T')[0]
        }),
        clientsService.getAll(),
        tasksService.getTaskStats(),
        communicationsService.getCommunicationStats()
      ]);

      // Process workflow analytics
      const workflowAnalytics = processWorkflowAnalytics(workflowStages, allClients, allTasks);
      
      // Process task analytics
      const taskAnalytics = processTaskAnalytics(allTasks, taskStats);
      
      // Process communication analytics
      const communicationAnalytics = processCommunicationAnalytics(allCommunications, commStats);
      
      // Process client analytics
      const clientAnalytics = processClientAnalytics(allClients, workflowStages);

      setAnalytics({
        workflow: workflowAnalytics,
        tasks: taskAnalytics,
        communications: communicationAnalytics,
        clients: clientAnalytics
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processWorkflowAnalytics = (stages, clients, tasks) => {
    const stageDistribution = {};
    const stageTaskCompletion = {};
    const avgTimeInStage = {};

    stages.forEach(stage => {
      const stageClients = clients.filter(client => client.workflow_stage_id === stage.id);
      const stageTasks = tasks.filter(task => task.workflow_stage_id === stage.id);
      const completedTasks = stageTasks.filter(task => task.status === 'completed');

      stageDistribution[stage.name] = stageClients.length;
      stageTaskCompletion[stage.name] = {
        total: stageTasks.length,
        completed: completedTasks.length,
        percentage: stageTasks.length > 0 ? Math.round((completedTasks.length / stageTasks.length) * 100) : 0
      };
    });

    return {
      stageDistribution,
      stageTaskCompletion,
      avgTimeInStage,
      totalStages: stages.length,
      mostPopularStage: Object.keys(stageDistribution).reduce((a, b) => 
        stageDistribution[a] > stageDistribution[b] ? a : b, Object.keys(stageDistribution)[0]
      )
    };
  };

  const processTaskAnalytics = (tasks, stats) => {
    const priorityDistribution = {
      high: tasks.filter(t => t.priority === 'high').length,
      medium: tasks.filter(t => t.priority === 'medium').length,
      low: tasks.filter(t => t.priority === 'low').length
    };

    const statusDistribution = {
      pending: tasks.filter(t => t.status === 'pending').length,
      in_progress: tasks.filter(t => t.status === 'in_progress').length,
      completed: tasks.filter(t => t.status === 'completed').length,
      cancelled: tasks.filter(t => t.status === 'cancelled').length
    };

    const categoryDistribution = {};
    tasks.forEach(task => {
      const category = task.category || 'general';
      categoryDistribution[category] = (categoryDistribution[category] || 0) + 1;
    });

    const completionRate = tasks.length > 0 ? 
      Math.round((statusDistribution.completed / tasks.length) * 100) : 0;

    return {
      ...stats,
      priorityDistribution,
      statusDistribution,
      categoryDistribution,
      completionRate,
      totalTasks: tasks.length
    };
  };

  const processCommunicationAnalytics = (communications, stats) => {
    const typeDistribution = {
      email: communications.filter(c => c.communication_type === 'email').length,
      phone: communications.filter(c => c.communication_type === 'phone').length,
      meeting: communications.filter(c => c.communication_type === 'meeting').length,
      text: communications.filter(c => c.communication_type === 'text').length,
      other: communications.filter(c => c.communication_type === 'other').length
    };

    const directionDistribution = {
      inbound: communications.filter(c => c.direction === 'inbound').length,
      outbound: communications.filter(c => c.direction === 'outbound').length
    };

    // Group by date for trend analysis
    const dailyCommunications = {};
    communications.forEach(comm => {
      const date = new Date(comm.communication_date).toISOString().split('T')[0];
      dailyCommunications[date] = (dailyCommunications[date] || 0) + 1;
    });

    return {
      ...stats,
      typeDistribution,
      directionDistribution,
      dailyCommunications,
      totalCommunications: communications.length
    };
  };

  const processClientAnalytics = (clients, stages) => {
    const statusDistribution = {
      active: clients.filter(c => c.status === 'active').length,
      inactive: clients.filter(c => c.status === 'inactive').length
    };

    const stageDistribution = {};
    stages.forEach(stage => {
      stageDistribution[stage.name] = clients.filter(c => c.workflow_stage_id === stage.id).length;
    });

    return {
      statusDistribution,
      stageDistribution,
      totalClients: clients.length,
      activeClients: statusDistribution.active
    };
  };

  const formatPercentage = (value, total) => {
    if (total === 0) return '0%';
    return `${Math.round((value / total) * 100)}%`;
  };

  const getChartColor = (index) => {
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6b7280'
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Workflow Analytics</h2>
          <p className="mt-1 text-sm text-gray-600">
            Comprehensive insights into your workflow performance and client management
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">👥</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Clients</dt>
                  <dd className="text-lg font-medium text-gray-900">{analytics.clients.totalClients || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">✅</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Task Completion Rate</dt>
                  <dd className="text-lg font-medium text-gray-900">{analytics.tasks.completionRate || 0}%</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-semibold text-sm">💬</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Communications</dt>
                  <dd className="text-lg font-medium text-gray-900">{analytics.communications.totalCommunications || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold text-sm">🔄</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Workflow Stages</dt>
                  <dd className="text-lg font-medium text-gray-900">{analytics.workflow.totalStages || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Stage Distribution */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Client Distribution by Stage</h3>
            <div className="space-y-3">
              {Object.entries(analytics.workflow.stageDistribution || {}).map(([stage, count], index) => (
                <div key={stage} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getChartColor(index) }}
                    ></div>
                    <span className="text-sm font-medium text-gray-900">{stage}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{count} clients</span>
                    <span className="text-xs text-gray-400">
                      ({formatPercentage(count, analytics.clients.totalClients)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Task Priority Distribution */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Task Priority Distribution</h3>
            <div className="space-y-3">
              {Object.entries(analytics.tasks.priorityDistribution || {}).map(([priority, count], index) => (
                <div key={priority} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getChartColor(index) }}
                    ></div>
                    <span className="text-sm font-medium text-gray-900 capitalize">{priority}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{count} tasks</span>
                    <span className="text-xs text-gray-400">
                      ({formatPercentage(count, analytics.tasks.totalTasks)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Communication Types */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Communication Types</h3>
            <div className="space-y-3">
              {Object.entries(analytics.communications.typeDistribution || {}).map(([type, count], index) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getChartColor(index) }}
                    ></div>
                    <span className="text-sm font-medium text-gray-900 capitalize">{type}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{count}</span>
                    <span className="text-xs text-gray-400">
                      ({formatPercentage(count, analytics.communications.totalCommunications)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Task Status Overview */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Task Status Overview</h3>
            <div className="space-y-3">
              {Object.entries(analytics.tasks.statusDistribution || {}).map(([status, count], index) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: getChartColor(index) }}
                    ></div>
                    <span className="text-sm font-medium text-gray-900">
                      {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">{count} tasks</span>
                    <span className="text-xs text-gray-400">
                      ({formatPercentage(count, analytics.tasks.totalTasks)})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Stage Performance Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Stage Performance</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Clients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Tasks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completed Tasks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Completion Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(analytics.workflow.stageTaskCompletion || {}).map(([stage, data]) => (
                  <tr key={stage}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {stage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {analytics.workflow.stageDistribution[stage] || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {data.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {data.completed}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <span>{data.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowAnalytics;